/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FiFrown, FiEye, FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import { BeatLoader } from 'react-spinners';
import { Editor } from '@tinymce/tinymce-react';
import { useAuth } from '../../../hooks/auth';
import { uploadFiles } from '../../../services/api';

import {
  getAllTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from '../../../services/api';
import {
  AlertWrapper,
  Modal,
  ModalContent,
  ActionButton,
  ButtonGroup,
  StyledInput,
  CheckboxGroup,
} from './styles';
import { useToast } from '../../../hooks/toast';

interface ITemplate {
  id?: string;
  title: string;
  htmlContent: string;
  requiresReadConfirmation: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const TinyMCEField = ({ name, ...rest }) => {
  const editorRef = React.useRef<any>(null);

  React.useEffect(() => {
    editorRef.current = rest.editorRef;
  }, [rest.editorRef]);

  const handleInit = (_evt, editor) => {
    editorRef.current = editor;
  };

  return <Editor onInit={handleInit} initialValue={rest.value} {...rest} />;
};

const TemplatesList: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const emptyTemplate = {
    title: '',
    htmlContent: '',
    requiresReadConfirmation: false,
  };
  const [formData, setFormData] = useState<ITemplate>(emptyTemplate);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getAllTemplates();
      setTemplates(data);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Atenção!',
        description: 'Não foi possível carregar os templates, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    getData();
  }, [getData]);

  const truncateText = (text: string, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const handleViewContent = (template: ITemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleOpenForm = (template?: ITemplate) => {
    if (template) {
      setFormData(template);
    } else {
      setFormData(emptyTemplate);
    }
    setIsFormModalOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormModalOpen(false);
    setFormData(emptyTemplate);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEditorChange = (content: string) => {
    setFormData(prev => ({
      ...prev,
      htmlContent: content,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (formData.id) {
        await updateTemplate(formData);
        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Template atualizado com sucesso.',
        });
      } else {
        await createTemplate(formData);
        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Template criado com sucesso.',
        });
      }
      handleCloseForm();
      getData();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: 'Não foi possível salvar o template, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        setLoading(true);
        await deleteTemplate(id);
        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Template excluído com sucesso.',
        });
        getData();
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: 'Não foi possível excluir o template, tente novamente.',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, template: ITemplate) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewContent(template);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '30px' }}>
        <BeatLoader size={16} />
      </div>
    );
  }

  return (
    <>
      <ButtonGroup>
        <ActionButton onClick={() => handleOpenForm()}>
          <FiPlus size={20} />
          Novo Template
        </ActionButton>
      </ButtonGroup>

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Nome</th>
            <th>Conteúdo</th>
            <th>Confirmação de Leitura</th>
            <th>Data de Criação</th>
            <th>Última Atualização</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(template => (
            <tr
              key={template.id}
              onClick={() => handleViewContent(template)}
              onKeyPress={e => handleKeyPress(e, template)}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <td>{template.id}</td>
              <td>{template.title}</td>
              <td>{truncateText(template.htmlContent)}</td>
              <td>{template.requiresReadConfirmation ? 'Sim' : 'Não'}</td>
              <td>
                {template.createdAt &&
                  new Date(template.createdAt).toLocaleDateString('pt-BR')}
              </td>
              <td>
                {template.updatedAt &&
                  new Date(template.updatedAt).toLocaleDateString('pt-BR')}
              </td>
              <td>
                <ButtonGroup>
                  <ActionButton onClick={() => handleOpenForm(template)}>
                    <FiEdit2 size={20} />
                    Editar
                  </ActionButton>
                  <ActionButton onClick={() => handleDelete(template.id!)}>
                    <FiTrash2 size={20} />
                    Excluir
                  </ActionButton>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!loading && templates.length === 0 && (
        <AlertWrapper>
          <FiFrown size={50} />
          Sem dados para apresentação
        </AlertWrapper>
      )}

      {isModalOpen && selectedTemplate && (
        <Modal>
          <ModalContent>
            <header>
              <h2>{selectedTemplate.title}</h2>
              <button type="button" onClick={handleCloseModal}>
                ×
              </button>
            </header>
            <div
              dangerouslySetInnerHTML={{ __html: selectedTemplate.htmlContent }}
            />
          </ModalContent>
        </Modal>
      )}

      {isFormModalOpen && (
        <Modal>
          <ModalContent>
            <header>
              <h2>{formData.id ? 'Editar Template' : 'Novo Template'}</h2>
              <button type="button" onClick={handleCloseForm}>
                ×
              </button>
            </header>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Título</label>
                <StyledInput
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="htmlContent">Conteúdo</label>
                <Editor
                  apiKey="your-tinymce-api-key"
                  value={formData.htmlContent}
                  onEditorChange={handleEditorChange}
                  init={{
                    height: 400,
                    menubar: true,
                    plugins: [
                      'advlist autolink lists link image charmap print preview anchor',
                      'searchreplace visualblocks code fullscreen',
                      'insertdatetime media table paste code help wordcount',
                      'image imagetools',
                    ],
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image media | removeformat | help',
                    images_upload_handler: async (
                      blobInfo,
                      success,
                      failure,
                    ) => {
                      try {
                        const uploadDataToSave = new FormData();
                        uploadDataToSave.append('images', blobInfo.blob());

                        const response = await uploadFiles({
                          formData: uploadDataToSave,
                          provider_id:
                            user.provider?.id ||
                            'd377694c-e2ca-4586-8bec-f20ae5d995b9',
                          type: 'images',
                        });
                        if (response?.data?.[0]?.url) {
                          success(response.data[0].url);
                        } else {
                          throw new Error('URL da imagem não retornada');
                        }
                      } catch (error) {
                        addToast({
                          type: 'error',
                          title: 'Erro!',
                          description:
                            'Não foi possível fazer o upload da imagem.',
                        });
                        failure('Erro ao fazer upload da imagem');
                      }
                    },
                    image_advtab: true,
                    image_title: true,
                    automatic_uploads: true,
                    file_picker_types: 'image',
                    images_reuse_filename: true,
                    image_dimensions: true,
                    image_class_list: [
                      { title: 'Nenhum', value: '' },
                      { title: 'Responsiva', value: 'img-responsive' },
                      { title: 'Arredondada', value: 'img-rounded' },
                      { title: 'Circular', value: 'img-circle' },
                    ],
                    image_uploadtab: true,
                    image_caption: true,
                    image_styleformats: [
                      { title: 'Padrão', value: '' },
                      { title: 'Esquerda', value: 'float-left' },
                      { title: 'Direita', value: 'float-right' },
                      { title: 'Centralizada', value: 'center' },
                    ],
                    content_style: `
                      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif; }
                      img { max-width: 100%; height: auto; }
                      img.img-responsive { width: 100%; height: auto; }
                      img.img-rounded { border-radius: 6px; }
                      img.img-circle { border-radius: 50%; }
                      img.float-left { float: left; margin: 0 1em 1em 0; }
                      img.float-right { float: right; margin: 0 0 1em 1em; }
                      img.center { display: block; margin: 0 auto; }
                    `,
                  }}
                />
              </div>

              <div className="form-group">
                <CheckboxGroup>
                  <label>
                    <input
                      type="checkbox"
                      name="requiresReadConfirmation"
                      checked={formData.requiresReadConfirmation}
                      onChange={handleInputChange}
                    />
                    Requer confirmação de leitura
                  </label>
                </CheckboxGroup>
              </div>

              <ButtonGroup>
                <ActionButton type="submit" disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar'}
                </ActionButton>
                <ActionButton type="button" onClick={handleCloseForm}>
                  Cancelar
                </ActionButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export { TemplatesList };
